/**
 * PaymentField.tsx
 *
 * Payment field component for Paystack and SnapScan integration
 * Displays payment button and status, handles amount calculation
 */

import React, { useMemo, useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { BaseFieldProps } from './shared/types';
import { formatCurrency, centsToAmount } from '../../utils/paymentUtils';
import { useFormBuilderContext } from '../../contexts/FormBuilderContext';
import { fetchPlanDetails } from '../../services/paymentService';

export const PaymentField: React.FC<BaseFieldProps> = React.memo(({
  fieldConfig,
  mode,
  value, // Stores transaction ID after payment completion
  onChange,
  errors,
  disabled,
  hasError,
  openPaymentModal,
  paymentStatus,
  className = '',
  isPaymentForm = false, // Display-only mode when part of payment-enabled form
  preloadedAmount,
}) => {
  const context = useFormBuilderContext();
  const appConfig = context?.appConfig;

  // Calculate amount based on config (fixed amount)
  // Dynamic amounts will be calculated by parent component with access to all form values
  const amount = useMemo(() => {
    if (!fieldConfig.amountConfig) return 0;

    if (fieldConfig.amountConfig.type === 'fixed' && fieldConfig.amountConfig.amount) {
      // Amount is stored in decimal, convert to cents
      return fieldConfig.amountConfig.amount * 100;
    }

    return 0; // Dynamic amounts calculated by parent
  }, [fieldConfig.amountConfig]);

  // Fetch plan amount from Paystack when amountConfig.type === 'plan'
  const planCode = fieldConfig.planConfig?.planCode || fieldConfig.amountConfig?.planCode;
  const [planAmount, setPlanAmount] = useState<number>(0);
  const [planInterval, setPlanInterval] = useState<string | null>(null);
  const [fetchingPlan, setFetchingPlan] = useState(false);

  useEffect(() => {
    if (!planCode || fieldConfig.amountConfig?.type !== 'plan') return;

    const apiUrl = appConfig?.features?.payment?.apiBaseUrl || appConfig?.backend_api_base_url || appConfig?.api_base_url; //TODO
    if (!apiUrl) return;

    setFetchingPlan(true);

    fetchPlanDetails(planCode, apiUrl).then((details) => {
      if (details && details.amount > 0) {
        setPlanAmount(details.amount);
        setPlanInterval(details.interval || null);
      }
      setFetchingPlan(false);
    });
  }, [planCode, fieldConfig.amountConfig?.type, appConfig?.features?.payment?.apiBaseUrl, appConfig?.backend_api_base_url, appConfig?.api_base_url]);

  const currency = fieldConfig.amountConfig?.currency || 'ZAR';

  // Precedence: what the server actually charged > the Paystack plan > the
  // amount configured on the field.
  //
  // `preloadedAmount` is the figure the pre-initialize webhook bound to the
  // transaction, and it is the only one that can be right for a server-priced
  // field. Those fields are configured `{ type: 'fixed', amount: 0 }` because
  // the real amount depends on answers collected in the form, so falling back
  // to amountConfig renders R0.00 — most visibly as "Amount Paid R0.00" on a
  // payment that in fact took hundreds of rand.
  const displayAmount =
    preloadedAmount !== undefined && preloadedAmount > 0
      ? Math.round(preloadedAmount * 100)
      : planAmount > 0
        ? planAmount
        : amount;

  // Every amount readout goes through this. An amount of 0 is not a price of
  // zero — it means nothing has resolved one yet — so it must never be
  // formatted as currency. The pending state already did this; the paid,
  // cancelled, and failed states did not, and showed R0.00.
  const amountReadout = (fallback: string) =>
    displayAmount > 0
      ? formatCurrency(displayAmount, currency)
      : <span className="text-base font-normal text-gray-400">{fallback}</span>;

  // Payment status based on value
  const isPaid = !!value;
  const transactionId = value as string;

  // Preview mode - show non-interactive field
  if (mode === 'preview') {
    const isConfigured = fieldConfig.amountConfig && (
      (fieldConfig.amountConfig.type === 'fixed' && fieldConfig.amountConfig.amount && fieldConfig.amountConfig.amount > 0) ||
      (fieldConfig.amountConfig.type === 'dynamic' && fieldConfig.amountConfig.expression) ||
      (fieldConfig.amountConfig.type === 'plan' && (fieldConfig.amountConfig.planCode || fieldConfig.planConfig?.planCode))
    );

    return (
      <div className={`payment-field-preview ${className}`}>
        <div className="space-y-2">
          {/* Label is shown in the field header in preview mode */}
          <div className={`border rounded-md p-4 ${isConfigured ? 'border-gray-300 bg-gray-50' : 'border-orange-300 bg-orange-50'}`}>
            {!isConfigured ? (
              <div className="flex items-center gap-3">
                <svg className="h-5 w-5 text-orange-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div>
                  <div className="text-sm font-semibold text-orange-900">Payment Not Configured</div>
                  <div className="text-xs text-orange-700 mt-1">
                    Please configure the payment amount in the field settings panel →
                  </div>
                </div>
              </div>
            ) : (
              <>
                {(fieldConfig.planConfig?.planCode || fieldConfig.amountConfig?.planCode) && (
                  <div className="flex items-center gap-1.5 mb-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                      Subscription
                    </span>
                    <span className="text-xs text-gray-400">Plan: {fieldConfig.planConfig?.planCode || fieldConfig.amountConfig?.planCode}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div className="text-gray-600">
                    <span className="text-xs uppercase tracking-wide">Amount:</span>
                    <div className="text-lg font-semibold mt-1">
                      {fetchingPlan ? (
                        <span className="text-base text-gray-400">Loading...</span>
                      ) : displayAmount > 0 ? (
                        <>
                          {formatCurrency(displayAmount, currency)}
                          {planInterval && <span className="text-xs font-normal text-gray-400 ml-1">/ {planInterval}</span>}
                        </>
                      ) : (
                        fieldConfig.amountConfig?.type === 'dynamic' ? 'Calculated at runtime'
                        : 'Not set'
                      )}
                    </div>
                  </div>
                  {!isPaymentForm && (
                    <button
                      type="button"
                      className="px-4 py-2 bg-gray-300 text-gray-600 rounded-md cursor-not-allowed"
                      disabled
                    >
                      Pay Now
                    </button>
                  )}
                </div>
                {fieldConfig.paymentDescription && (
                  <p className="text-sm text-gray-500 mt-3 border-t border-gray-200 pt-3">
                    {fieldConfig.paymentDescription}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Interactive mode - actual payment field
  return (
    <div className={`payment-field ${className}`}>
      <div className="space-y-2">
        {/* Label */}
        <label className="block text-sm font-medium text-gray-700">
          {fieldConfig.label}
          {fieldConfig.required && <span className="text-red-500 ml-1">*</span>}
        </label>

        {/* Payment container */}
        <div className={`border rounded-md p-4 ${
          paymentStatus === 'failed' ? 'border-red-300 bg-red-50'
          : paymentStatus === 'cancelled' ? 'border-gray-300 bg-gray-100'
          : hasError ? 'border-red-500 bg-red-50'
          : 'border-gray-300 bg-white'
        }`}>
          {/* Payment completed state */}
          {isPaid ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <svg
                    className="h-8 w-8 text-green-500"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
                <div>
                  <div className="text-sm font-medium text-green-700">
                    Payment Completed
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    Transaction ID: {transactionId}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-wide text-gray-500">
                  Amount Paid
                </div>
                <div className="text-lg font-semibold text-gray-900 mt-0.5">
                  {amountReadout('See your receipt')}
                </div>
              </div>
            </div>
          ) : paymentStatus === 'cancelled' ? (
            /* Payment not completed state (user closed popup — could be intentional cancel or after a decline) */
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-8 w-8 text-gray-400"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-700">
                      Payment Not Completed
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Your payment was not completed.
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs uppercase tracking-wide text-gray-500">
                    Amount Due
                  </div>
                  <div className="text-lg font-semibold text-gray-900 mt-0.5">
                    {amountReadout('Calculated at checkout')}
                  </div>
                </div>
              </div>
              <button
                type="button"
                className={`w-full px-6 py-3 rounded-md font-medium transition-colors ${
                  disabled || !openPaymentModal
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800'
                }`}
                disabled={disabled || !openPaymentModal}
                onClick={() => openPaymentModal?.()}
              >
                Try Again
              </button>
            </div>
          ) : paymentStatus === 'failed' ? (
            /* Payment failed state */
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-8 w-8 text-red-500"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-red-700">
                      Payment Failed
                    </div>
                    <div className="text-xs text-red-500 mt-0.5">
                      Verification failed. Please try again.
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs uppercase tracking-wide text-gray-500">
                    Amount Due
                  </div>
                  <div className="text-lg font-semibold text-gray-900 mt-0.5">
                    {amountReadout('Calculated at checkout')}
                  </div>
                </div>
              </div>
              <button
                type="button"
                className={`w-full px-6 py-3 rounded-md font-medium transition-colors ${
                  disabled || !openPaymentModal
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800'
                }`}
                disabled={disabled || !openPaymentModal}
                onClick={() => openPaymentModal?.()}
              >
                Try Again
              </button>
            </div>
          ) : (
            /* Payment pending state */
            <div className="space-y-3">
              {(fieldConfig.planConfig?.planCode || fieldConfig.amountConfig?.planCode) && (
                <div className="flex items-center gap-1.5">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                    Subscription
                  </span>
                  <span className="text-xs text-gray-400">Plan: {fieldConfig.planConfig?.planCode || fieldConfig.amountConfig?.planCode}</span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500">
                    Amount Due
                  </div>
                  <div className="text-2xl font-bold text-gray-900 mt-1">
                    {fetchingPlan ? (
                      <span className="text-base text-gray-400">Loading plan details...</span>
                    ) : displayAmount > 0 ? (
                      <>
                        {formatCurrency(displayAmount, currency)}
                        {planInterval && <span className="text-sm font-normal text-gray-400 ml-1">/ {planInterval}</span>}
                      </>
                    ) : (
                      <span className="text-base text-gray-400">Amount will be calculated</span>
                    )}
                  </div>
                </div>
                {/* Always show "Pay Now" button when not paid, regardless of isPaymentForm setting */}
                <button
                  type="button"
                  className={`px-6 py-3 rounded-md font-medium transition-colors flex items-center justify-center ${
                    disabled || !openPaymentModal || paymentStatus === 'processing'
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800'
                  }`}
                  disabled={disabled || !openPaymentModal || paymentStatus === 'processing'}
                  onClick={() => {
                    if (openPaymentModal) {
                      openPaymentModal();
                    }
                  }}
                >
                  {paymentStatus === 'processing' ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    'Pay Now'
                  )}
                </button>
              </div>

              {/* Description */}
              {fieldConfig.paymentDescription && (
                <div className="border-t border-gray-200 pt-3">
                  <p className="text-sm text-gray-600">
                    {fieldConfig.paymentDescription}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Help text */}
        {fieldConfig.description && !isPaid && (
          <p className="text-xs text-gray-500 mt-1">{fieldConfig.description}</p>
        )}

        {/* Errors */}
        {errors && errors.length > 0 && (
          <div className="mt-2 space-y-1">
            {errors.map((error, i) => (
              <p key={i} className="text-sm text-red-600 flex items-start">
                <svg
                  className="h-4 w-4 text-red-500 mr-1 flex-shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                {error}
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

PaymentField.displayName = 'PaymentField';
